/* Related products: a manual row that loops, like the homepage and blog
   strips but without autoplay (user decision 2026-09-23).

   The loop mechanics follow section-blog-product-recs.js: clones are appended
   after the last card, and once a scroll settles in that tail it snaps back
   by exactly one loop width, which lands on the same card image. Unlike that
   script it clones the whole set, not one viewport: a buyer can swipe to any
   card and then press next, and a full viewport forward from any resting
   card must still be inside the scroll range (with 20 cards and 5 per view
   the furthest target is card 24 of a 40-card track, 35 reachable). The
   arrows never disable and wrap both ways. That script is not reused because
   it serves one strip per page, initialises once at page load and ties the
   loop to autoplay; these cards are injected later by
   product-recommendations, so the loop lives in a custom element that sets
   itself up whenever its markup is connected (first load and theme editor
   re-renders alike).

   Each arrow click moves one viewport of whole cards (5 / 3 / 2 at the
   desktop / tablet / phone sizes). With too few cards to fill more than a
   viewport the row stays a plain Dawn slider. */

/* Hover on a card whose variants have their own images (user decision
   2026-10-03): the image steps through them, about one a second, and goes
   back to the card's own image when the pointer leaves. The list comes from
   snippets/card-product.liquid (variant_images). Cards without one keep
   Dawn's hover, which shows the product's second image.

   Only for a mouse at the width where Dawn's card hover applies (990px, see
   component-card.css), and not with reduced motion; everywhere else the card
   behaves as before, second image included. The pointer type is checked on
   each event rather than with a hover media query, so a laptop with a touch
   screen still cycles under the mouse and never under a finger.

   The first change comes as soon as the next image has loaded, like Dawn's
   swap to the second image, then one per interval. Images are requested on
   hover, one ahead of the one on screen, and each stays in the card as a
   stacked img once loaded, so going round again only flips a class. One that
   fails to load is skipped for the rest of that hover and requested again on
   the next.

   Each step first checks that the pointer is still on the card. It can leave
   without a pointerleave, for instance when the row scrolls under a pointer
   that does not move (a trackpad swipe, the loop's snap back); the cycle
   then stops instead of running on a card nobody points at. */
if (!customElements.get('related-products-loop')) {
  class RelatedProductsVariantCycle {
    static interval = 1000;
    static widths = [165, 360, 533, 720, 940, 1066];

    constructor(root) {
      this.root = root;
      this.onPointerOver = this.onPointerOver.bind(this);
      this.stop = this.stop.bind(this);
      root.addEventListener('pointerover', this.onPointerOver);
    }

    destroy() {
      this.stop();
      this.root.removeEventListener('pointerover', this.onPointerOver);
    }

    onPointerOver(event) {
      if (event.pointerType !== 'mouse') return;
      const card = event.target.closest('.card-wrapper');
      if (!card || card === this.card) return;
      this.stop();

      const media = card.querySelector('.media[data-variant-images]');
      if (!media) return;
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let sources;
      try {
        sources = JSON.parse(media.dataset.variantImages);
      } catch (error) {
        return;
      }
      if (!Array.isArray(sources) || !sources.length) return;

      this.card = card;
      this.media = media;
      this.sources = sources;
      this.primary = media.querySelector('img');
      // frame 0 is the card's own image, frames 1..n the variant images
      this.frames = media.relatedProductsFrames ??= [];
      this.frames.forEach((frame, index) => {
        if (!frame?.failed) return;
        frame.image.remove();
        this.frames[index] = undefined;
      });
      this.position = 0;
      media.classList.add('media--variant-cycle');
      card.addEventListener('pointerleave', this.stop);
      this.advance();
    }

    stop() {
      clearTimeout(this.timer);
      this.token = null;
      if (!this.card) return;
      this.card.removeEventListener('pointerleave', this.stop);
      this.media.classList.remove('media--variant-cycle');
      this.show(0);
      this.card = this.media = this.sources = this.primary = this.frames = null;
    }

    advance() {
      const token = (this.token = {});
      this.position = (this.position + 1) % (this.sources.length + 1);
      if (this.position === 0) {
        this.show(0);
        this.schedule(token);
        return;
      }
      const position = this.position;
      this.load(position).then((ready) => {
        if (token !== this.token) return;
        if (ready) this.show(position);
        this.schedule(token);
        if (position < this.sources.length) this.load(position + 1);
      });
    }

    schedule(token) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (token !== this.token) return;
        if (this.card.matches(':hover')) this.advance();
        else this.stop();
      }, RelatedProductsVariantCycle.interval);
    }

    // Resolves true once the frame's image is decoded, false if it failed.
    load(position) {
      if (this.frames[position]) return this.frames[position].ready;
      const [url, width] = this.sources[position - 1];
      const join = url.includes('?') ? '&' : '?';
      const srcset = RelatedProductsVariantCycle.widths
        .filter((candidate) => candidate < width)
        .map((candidate) => `${url}${join}width=${candidate} ${candidate}w`)
        .concat(`${url} ${width}w`)
        .join(', ');
      const image = document.createElement('img');
      image.className = 'related-products__variant-image';
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      image.decoding = 'async';
      image.sizes = this.primary?.sizes || '';
      image.srcset = srcset;
      image.src = `${url}${join}width=533`;
      this.media.appendChild(image);
      const frame = { image };
      frame.ready = image.decode().then(
        () => true,
        () => {
          frame.failed = true;
          return false;
        }
      );
      this.frames[position] = frame;
      return frame.ready;
    }

    // One image visible at a time, as in Dawn's hover: product shots can have
    // transparent backgrounds, so nothing may show through from below.
    show(position) {
      this.media?.classList.toggle('media--variant-shown', position > 0);
      this.frames?.forEach((frame, index) => frame?.image.classList.toggle('is-shown', index === position));
    }
  }

  customElements.define(
    'related-products-loop',
    class RelatedProductsLoop extends HTMLElement {
      connectedCallback() {
        this.variantCycle = new RelatedProductsVariantCycle(this);
        this.sliderComponent = this.querySelector('slider-component');
        if (!this.sliderComponent) return;
        // When product-recommendations injects the markup, this element is
        // connected before the slider-component inside it is upgraded. Its
        // constructor would then run after build() and reset
        // enableSliderLooping to false and disable the end arrows (a disabled
        // button gets no clicks). Upgrade it now; this is a no-op when it
        // already is.
        customElements.upgrade(this.sliderComponent);
        this.slider = this.sliderComponent.querySelector('.slider');
        if (!this.slider) return;
        this.realSlides = Array.from(this.slider.querySelectorAll('.slider__slide'));
        if (this.realSlides.length < 2) return;

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.onClick = this.onClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);

        this.build();
        this.sliderComponent.addEventListener('click', this.onClick, true);
        this.slider.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        this.variantCycle?.destroy();
        this.variantCycle = null;
        window.removeEventListener('resize', this.onResize);
        clearTimeout(this.settleTimer);
        clearTimeout(this.resizeTimer);
      }

      measure() {
        // Fractional sizes: offsetLeft/offsetWidth round each card to whole
        // pixels, and over five cards that is enough to count 4.99 per view.
        const first = this.realSlides[0].getBoundingClientRect();
        const second = this.realSlides[1].getBoundingClientRect();
        this.step = second.left - first.left;
        this.loopWidth = this.step * this.realSlides.length;
        // (width + gap) per card against (n cards + n-1 gaps) per view: add one
        // gap before dividing so five whole cards count as five, and allow a
        // hair of rounding; the 2.3 and 3.3 peeks still floor to 2 and 3.
        const gap = this.step - first.width;
        const view = this.slider.getBoundingClientRect().width;
        this.perView = Math.max(1, Math.floor((view + gap) / this.step + 0.02));
        return this.step > 0;
      }

      build() {
        this.slider.querySelectorAll('.related-products-loop__clone').forEach((clone) => clone.remove());
        this.looping = false;
        if (!this.measure()) return;

        // Clones would sit on screen next to their originals, and the loop
        // point would lie beyond the scroll range.
        const perViewportCeil = Math.ceil(this.slider.clientWidth / this.step);
        if (this.realSlides.length <= perViewportCeil + 1) {
          this.sliderComponent.enableSliderLooping = false;
          this.sliderComponent.update?.();
          return;
        }

        for (let i = 0; i < this.realSlides.length; i++) {
          const clone = this.realSlides[i].cloneNode(true);
          clone.removeAttribute('id');
          clone.setAttribute('aria-hidden', 'true');
          clone.classList.add('related-products-loop__clone');
          // variant images a hover has loaded so far; the clone loads its own
          clone.querySelectorAll('.related-products__variant-image').forEach((image) => image.remove());
          // no duplicate ids, and nothing in a hidden clone may take focus
          clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
          clone.querySelectorAll('a, button, input, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
          this.slider.appendChild(clone);
        }

        // Dawn's SliderComponent leaves its disable/enable arrow logic alone
        // when looping is on; clear whatever state it has already applied.
        this.sliderComponent.enableSliderLooping = true;
        this.sliderComponent.querySelectorAll('.slider-button').forEach((button) => button.removeAttribute('disabled'));
        this.looping = true;
      }

      scrollToLeft(left, smooth) {
        this.slider.scrollTo({ left, behavior: smooth && !this.reduceMotion ? 'smooth' : 'instant' });
      }

      onClick(event) {
        if (!this.looping) return;
        const button = event.target.closest?.('button[name="previous"], button[name="next"]');
        if (!button) return;
        // Capture phase: Dawn's own button handler never runs, its
        // scrollLeft + step arithmetic would drift off the card grid.
        event.stopPropagation();

        const cards = button.name === 'previous' ? -this.perView : this.perView;
        let from = Math.round(this.slider.scrollLeft / this.step);
        if (from >= this.realSlides.length) {
          // clicked before the settle snap-back ran: do it now
          from -= this.realSlides.length;
          this.scrollToLeft(from * this.step, false);
        }
        if (cards < 0 && from + cards < 0) {
          // wrap to the identical position one loop further on, then move back
          from += this.realSlides.length;
          this.scrollToLeft(from * this.step, false);
        }
        this.scrollToLeft((from + cards) * this.step, true);
      }

      onScroll() {
        if (!this.looping) return;
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          // half a card of slack: snap points land on rounded pixels
          if (this.slider.scrollLeft >= this.loopWidth - this.step / 2) {
            this.scrollToLeft(this.slider.scrollLeft - this.loopWidth, false);
          }
        }, 150);
      }

      onResize() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          this.variantCycle?.stop();
          const left = Math.round(this.slider.scrollLeft / this.step) % this.realSlides.length;
          this.build();
          if (this.step) this.scrollToLeft(left * this.step, false);
        }, 200);
      }
    }
  );
}
